/**
 * AtlasScreen — экран открытий: всё что игрок собрал по категориям.
 *
 * JB 2026-05-25: «в атласе открытий должно быть всё в разных категориях
 * от открытых эффектов в виде прогресс баров по эффектам отдельных
 * категорий с открытием детализации по клику».
 *
 * Архитектура:
 *   - 5 категорий: фразы / уроки / артефакты / апгрейды / шёпоты
 *   - Каждая категория = CategoryCard с progress bar (X из Y) и chevron
 *   - Клик по карточке → expand с детализацией: open elements тёплым
 *     текстом, locked — silhouette «???» (но с тонким намёком что есть)
 *   - Заголовок экрана + кнопка close (возврат к Spark)
 *
 * Apgrades-экспансия: «Атлас крылатый» апгрейд (u030) уже даёт
 * tap multiplier по всем собранным фразам. Дальше будем добавлять
 * подкатегории апгрейдов атласа в Wave 2 каталога.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ALL_PHRASES, LESSONS_ALPHA, UPGRADES_ALPHA, WHISPERS_ALPHA } from '@sai-clicker/content';
import { describeUpgradeEffect, getUpgradeLevel, type PhraseEntry } from '@sai-clicker/game-core';
import { useGameStore } from '../stores/game.js';
import { useArtifactsStore } from '../visual-artifacts/store.js';
import { VISUAL_ARTIFACTS_CATALOG } from '../visual-artifacts/catalog.js';
import { ArtifactPreview } from '../visual-artifacts/ArtifactPreview.js';
import { useNavigationStore } from './navigation.js';
import {
  computeReward,
  getRecentlyOpened,
  pickDailyFeatured,
  useAtlasRewardsStore,
  type AtlasCategoryKey,
  type AtlasReward,
} from './atlasRewards.js';
import { nextUnlockFor } from './atlasNextUnlock.js';
import styles from './AtlasScreen.module.css';

type CategoryKey = AtlasCategoryKey;

interface CategoryEntry {
  readonly id: string;
  readonly title: string;
  /** Subtitle for unlocked entries — usually description / effect. */
  readonly subtitle: string;
  readonly unlocked: boolean;
  /** Optional reward metadata for reward computation. */
  readonly phraseSpecial?: PhraseEntry['special'];
  readonly artifactWeight?: 'major' | 'minor';
  /** Если задан — entry рендерит мини-превью артефакта слева от текста. */
  readonly artifactPreviewId?: string;
  /**
   * Сколько раз игрок видел эту запись в игре (для phrases — sighting count
   * из atlasCollected map). Doc 28 §3: surface frequency как ощутимый
   * collection-сигнал. 0 = ни разу (locked), ≥1 = открыто.
   */
  readonly sightingCount?: number;
}

interface CategorySummary {
  readonly key: CategoryKey;
  readonly label: string;
  readonly icon: string;
  readonly opened: number;
  readonly total: number;
  readonly entries: readonly CategoryEntry[];
}

export function AtlasScreen(): JSX.Element {
  const gameState = useGameStore((s) => s.state);
  const atlasCollected = gameState.atlasCollected;
  const lessonsUnlocked = gameState.lessonsUnlocked;
  const purchasedUpgrades = gameState.purchasedUpgrades;
  const grantAtlasReward = useGameStore((s) => s.grantAtlasReward);
  const unlockedArtifacts = useArtifactsStore((s) => s.unlocked);
  const setScreen = useNavigationStore((s) => s.setScreen);
  const isViewed = useAtlasRewardsStore((s) => s.isViewed);
  const markViewed = useAtlasRewardsStore((s) => s.markViewed);
  const openedAt = useAtlasRewardsStore((s) => s.openedAt);
  const totalAwarenessesEarned = useAtlasRewardsStore((s) => s.totalAwarenessesEarned);
  const totalSpiritBallsEarned = useAtlasRewardsStore((s) => s.totalSpiritBallsEarned);

  const [expanded, setExpanded] = useState<CategoryKey | null>(null);
  const [lastReward, setLastReward] = useState<AtlasReward | null>(null);

  /**
   * Click handler — если entry unlocked и не viewed, начисляем reward.
   */
  function handleEntryClick(category: CategoryKey, entry: CategoryEntry): void {
    if (!entry.unlocked) return;
    if (isViewed(entry.id)) return;
    const reward = computeReward(category, entry.id, {
      phraseSpecial: entry.phraseSpecial,
      artifactWeight: entry.artifactWeight,
    });
    const wasFirst = markViewed(entry.id, reward);
    if (wasFirst && (reward.awarenesses > 0 || reward.spirit_balls > 0)) {
      grantAtlasReward(reward.awarenesses, reward.spirit_balls);
      setLastReward(reward);
      window.setTimeout(() => {
        setLastReward(null);
      }, 2500);
    }
  }

  // JB 2026-05-26 production fix: numeric layer phrases («+1», «+2», …) —
  // это не фразы-осознания, а тех. символы для floating-text счётчика
  // (state.ts подменяет текст на реальное число при тапе). В Atlas их
  // показ как «открытий» путал игрока («хотя это вообще не фразы»).
  // Решение: исключить numeric layer из категории «фразы-осознания» —
  // total и opened считаются только по смысловым слоям.
  const collectiblePhrases = ALL_PHRASES.filter((p) => p.layer !== 'numeric');
  const collectiblePhraseIds = new Set(collectiblePhrases.map((p) => p.id));
  let collectiblePhrasesOpened = 0;
  for (const id of atlasCollected.keys()) {
    if (collectiblePhraseIds.has(id)) collectiblePhrasesOpened += 1;
  }

  // Community whispers с `{placeholder}` (типа «{awarenessesToday}») — это
  // templating для WhisperTicker, который подставляет live numbers. В
  // Atlas они без подстановки выглядят как баг. Исключаем сразу.
  // Прочие community whispers без `{` (например «двенадцать учеников
  // вошли в Рощу Тела») остаются.
  const collectibleWhispers = WHISPERS_ALPHA.filter((w) => !w.text.includes('{'));

  const categories: readonly CategorySummary[] = [
    {
      key: 'phrases',
      label: 'фразы-осознания',
      icon: '◈',
      opened: collectiblePhrasesOpened,
      total: collectiblePhrases.length,
      entries: collectiblePhrases.map((p) => ({
        id: p.id,
        title: p.text,
        subtitle: `слой: ${p.layer} · эпоха ${p.epoch.toString()}`,
        unlocked: atlasCollected.has(p.id),
        phraseSpecial: p.special,
        sightingCount: atlasCollected.get(p.id) ?? 0,
      })),
    },
    {
      key: 'lessons',
      label: 'уроки',
      icon: '✦',
      opened: lessonsUnlocked.size,
      total: LESSONS_ALPHA.length,
      entries: LESSONS_ALPHA.map((l) => ({
        id: l.id,
        title: l.title,
        subtitle: `+${Math.round(l.bonus * 100).toString()}% к множителю`,
        unlocked: lessonsUnlocked.has(l.id),
      })),
    },
    {
      key: 'artifacts',
      label: 'артефакты пути',
      icon: '◉',
      opened: unlockedArtifacts.size,
      total: VISUAL_ARTIFACTS_CATALOG.length,
      entries: VISUAL_ARTIFACTS_CATALOG.map((a) => ({
        id: a.id,
        title: a.displayName,
        // JB 2026-05-26: показываем description (что физически есть в игре)
        // ВМЕСТЕ с revealLine (поэтическая фраза Сай). Раньше был только
        // revealLine — игрок не понимал ЧТО видимо изменилось.
        subtitle:
          a.description !== undefined ? `${a.description} — «${a.revealLine}»` : a.revealLine,
        unlocked: unlockedArtifacts.has(a.id),
        artifactWeight: a.weight,
        artifactPreviewId: a.id,
      })),
    },
    {
      key: 'upgrades',
      label: 'апгрейды',
      icon: '✧',
      opened: purchasedUpgrades.size,
      total: UPGRADES_ALPHA.length,
      entries: UPGRADES_ALPHA.map((u) => {
        const level = getUpgradeLevel(purchasedUpgrades, u.id);
        const effect = describeUpgradeEffect(u, level);
        const subtitle =
          level > 0
            ? (effect.currentLabel ?? u.description)
            : `${u.description} · купить за ${u.baseCost.toString()} ${u.currency === 'spirit_balls' ? 'духобаллов' : 'осознаний'}`;
        return {
          id: u.id,
          title: level > 0 ? `${u.name} · ур. ${level.toString()}` : u.name,
          subtitle,
          unlocked: level > 0,
        };
      }),
    },
    {
      key: 'whispers',
      label: 'шёпоты Рощи',
      icon: '◌',
      // Whispers don't have a "collected" set yet — alpha shows all as
      // unlocked для прозрачности (контент-зрелище). Будет расширено
      // когда добавим whisper-collection в game state.
      // 2026-05-26: фильтруем `{placeholder}` шёпоты — они для ticker'а,
      // не для Atlas display.
      opened: collectibleWhispers.length,
      total: collectibleWhispers.length,
      entries: collectibleWhispers.map((w) => ({
        id: w.id,
        title: w.text,
        subtitle: `категория: ${w.category}`,
        unlocked: true,
      })),
    },
  ];

  // JB doc 28 §I — Atlas oxygen.
  //
  // 1. Daily featured — детерминированно по дате выбираем одну unlocked
  //    фразу из коллекции игрока. Открытие Atlas — как ритуал «карточки
  //    дня», обновляется раз в сутки.
  // 2. Recent feed — последние 5 entry открытых за 24 часа. Создаёт
  //    feed-like dynamism, игрок видит свою активность.
  const allEntries = categories.flatMap((cat) => cat.entries);
  const unlockedEntries = allEntries.filter((e) => e.unlocked);
  // Defensive: исключаем entries с unsubstituted `{placeholder}` из featured
  // (теоретически уже отфильтрованы выше, но если попадёт новый whisper
  // с placeholder — он не сломает «карточку дня»).
  const featurableEntries = unlockedEntries.filter(
    (e) => !e.title.includes('{') && !e.subtitle.includes('{'),
  );
  const featured = pickDailyFeatured(featurableEntries);
  // Mapping id → entry для recent feed (быстрый lookup) + validIds set
  // для фильтрации orphan recently-opened (entry удалён из контента,
  // но openedAt timestamp остался в localStorage).
  const entryById = new Map(allEntries.map((e) => [e.id, e] as const));
  const validIds = new Set(allEntries.map((e) => e.id));
  const recent = getRecentlyOpened(openedAt, 5, 24, validIds);

  return (
    <main className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <p className={styles.preface}>память пути</p>
          <h1 className={styles.title}>атлас открытий</h1>
          {(totalAwarenessesEarned > 0 || totalSpiritBallsEarned > 0) && (
            <p className={styles.totalEarned}>
              получено: {totalAwarenessesEarned.toString()} осознаний
              {totalSpiritBallsEarned > 0
                ? ` · ${totalSpiritBallsEarned.toString()} духобаллов`
                : ''}
            </p>
          )}
        </div>
        <button
          type="button"
          className={styles.close}
          onClick={() => {
            setScreen('spark');
          }}
          aria-label="Закрыть атлас и вернуться к Искре"
        >
          ✕
        </button>
      </header>

      {/* «Карточка дня» — featured entry для open-ритуала. */}
      {featured !== null && (
        <section className={styles.featured} aria-label="Открытие дня">
          <p className={styles.featuredLabel}>сегодня в атласе</p>
          <p className={styles.featuredTitle}>{featured.title}</p>
          <p className={styles.featuredSubtitle}>{featured.subtitle}</p>
        </section>
      )}

      {/* «Открыто недавно» — feed-like последние 5 за 24h. */}
      {recent.length > 0 && (
        <section className={styles.recent} aria-label="Открыто недавно">
          <p className={styles.recentLabel}>открыто недавно</p>
          <ul className={styles.recentList}>
            {recent.map((r) => {
              const entry = entryById.get(r.entryId);
              if (entry === undefined) return null;
              const minutesAgo = Math.floor((Date.now() - r.openedAt) / 60000);
              const ago =
                minutesAgo < 1
                  ? 'только что'
                  : minutesAgo < 60
                    ? `${minutesAgo.toString()} мин назад`
                    : `${Math.floor(minutesAgo / 60).toString()} ч назад`;
              return (
                <li key={r.entryId} className={styles.recentItem}>
                  <span className={styles.recentTitle}>{entry.title}</span>
                  <span className={styles.recentTime}>{ago}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <ul className={styles.categoryList}>
        {categories.map((cat) => {
          const newCount = cat.entries.filter((e) => e.unlocked && !isViewed(e.id)).length;
          const nextHint = nextUnlockFor(cat.key, gameState, unlockedArtifacts);
          return (
            <CategoryCard
              key={cat.key}
              summary={cat}
              newCount={newCount}
              nextHint={nextHint}
              isExpanded={expanded === cat.key}
              onToggle={() => {
                setExpanded(expanded === cat.key ? null : cat.key);
              }}
              onEntryClick={(entry) => {
                handleEntryClick(cat.key, entry);
              }}
              isViewed={isViewed}
            />
          );
        })}
      </ul>

      {/* Reward toast — появляется на 2.5 сек после получения награды */}
      <AnimatePresence>
        {lastReward !== null && (
          <motion.div
            className={styles.rewardToast}
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.25, 0.8, 0.3, 1] }}
          >
            <p className={styles.rewardLine}>память благодарит</p>
            {lastReward.awarenesses > 0 && (
              <p className={styles.rewardAmount}>+{lastReward.awarenesses.toString()} осознаний</p>
            )}
            {lastReward.spirit_balls > 0 && (
              <p className={styles.rewardAmountGold}>
                +{lastReward.spirit_balls.toString()} духобаллов
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

interface CardProps {
  readonly summary: CategorySummary;
  readonly newCount: number;
  readonly nextHint: string | null;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  readonly onEntryClick: (entry: CategoryEntry) => void;
  readonly isViewed: (entryId: string) => boolean;
}

function CategoryCard({
  summary,
  newCount,
  nextHint,
  isExpanded,
  onToggle,
  onEntryClick,
  isViewed,
}: CardProps): JSX.Element {
  const progressPct = summary.total > 0 ? (summary.opened / summary.total) * 100 : 0;
  return (
    <li className={styles.card}>
      <button
        type="button"
        className={styles.cardHead}
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className={styles.cardIcon} aria-hidden>
          {summary.icon}
        </span>
        <span className={styles.cardLabel}>{summary.label}</span>
        {newCount > 0 && (
          <span
            className={styles.newBadge}
            aria-label={`${newCount.toString()} новых, можно забрать награду`}
          >
            {newCount.toString()} нов.
          </span>
        )}
        <span className={styles.cardCount}>
          {summary.opened.toString()} / {summary.total.toString()}
        </span>
        <span className={styles.cardChevron} aria-hidden>
          {isExpanded ? '▾' : '▸'}
        </span>
      </button>
      <div className={styles.progressTrack} aria-hidden>
        <div className={styles.progressFill} style={{ width: `${progressPct.toFixed(1)}%` }} />
      </div>
      {nextHint !== null && (
        <p className={styles.nextHint} aria-label={`Следующее открытие: ${nextHint}`}>
          → {nextHint}
        </p>
      )}

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.ul
            className={styles.entryList}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.8, 0.3, 1] }}
          >
            {summary.entries.map((entry) => {
              const isNew = entry.unlocked && !isViewed(entry.id);
              const itemClass = [
                styles.entry ?? '',
                !entry.unlocked ? (styles.entryLocked ?? '') : '',
                isNew ? (styles.entryNew ?? '') : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <li
                  key={entry.id}
                  className={itemClass}
                  onClick={() => {
                    onEntryClick(entry);
                  }}
                  role={isNew ? 'button' : undefined}
                  tabIndex={isNew ? 0 : undefined}
                  onKeyDown={
                    isNew
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onEntryClick(entry);
                          }
                        }
                      : undefined
                  }
                >
                  <div className={styles.entryRow}>
                    {/* Артефакт-превью слева для unlocked entries */}
                    {entry.unlocked && entry.artifactPreviewId !== undefined && (
                      <div className={styles.entryPreview}>
                        <ArtifactPreview artifactId={entry.artifactPreviewId} size={44} />
                      </div>
                    )}
                    <div className={styles.entryText}>
                      <p className={styles.entryTitle}>
                        {entry.unlocked ? entry.title : 'ещё откроется'}
                        {isNew && (
                          <span className={styles.entryNewMark} aria-hidden>
                            · нажми
                          </span>
                        )}
                        {entry.unlocked &&
                          entry.sightingCount !== undefined &&
                          entry.sightingCount >= 2 && (
                            <span
                              className={styles.entrySightings}
                              aria-label={`появлялась ${entry.sightingCount.toString()} раз`}
                            >
                              ×{entry.sightingCount.toString()}
                            </span>
                          )}
                      </p>
                      <p className={styles.entrySubtitle}>
                        {entry.unlocked ? entry.subtitle : '· · ·'}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
}
